<script>
  export let question;
  export let updateWorkingQuestion;

  let selectorValues, hasSelector;

  function getNodeSelectorValues() {
    const nodesHandler = question.dataset?.find(handler => handler[0] === 'nodes');
    if (nodesHandler && nodesHandler[1] && nodesHandler[1].selector) {
      return Array.isArray(nodesHandler[1].selector) ? nodesHandler[1].selector : [nodesHandler[1].selector];
    }
    return [];
  }
  
  function updateNodeSelectorValue(index, value) {
    const dataset = [...question.dataset];
    const nodesHandlerIndex = dataset.findIndex(handler => handler[0] === 'nodes');
    
    if (nodesHandlerIndex !== -1) {
      const handler = [...dataset[nodesHandlerIndex]];
      const props = { ...handler[1] };
      const selectors = Array.isArray(props.selector) ? [...props.selector] : [props.selector];
      selectors[index] = value;
      props.selector = selectors.length === 1 ? selectors[0] : selectors;
      handler[1] = props;
      dataset[nodesHandlerIndex] = handler;
      updateWorkingQuestion('dataset', dataset);
    }
  }
  
  function removeNodeSelectorValue(index) {
    const dataset = [...question.dataset];
    const nodesHandlerIndex = dataset.findIndex(handler => handler[0] === 'nodes');
    
    if (nodesHandlerIndex !== -1) {
      const handler = [...dataset[nodesHandlerIndex]];
      const props = { ...handler[1] };
      let selectors = Array.isArray(props.selector) ? [...props.selector] : [props.selector];
      
      // Remove the selector at the specified index
      selectors.splice(index, 1);
      
      if (selectors.length === 0) {
        // Remove selector property entirely if no selectors left
        delete props.selector;
      } else {
        // Update with remaining selectors
        props.selector = selectors.length === 1 ? selectors[0] : selectors;
      }
      
      handler[1] = props;
      dataset[nodesHandlerIndex] = handler;
      updateWorkingQuestion('dataset', dataset);
    }
  }
  
  function addNodeSelectorValue() {
    const dataset = [...question.dataset];
    const nodesHandlerIndex = dataset.findIndex(handler => handler[0] === 'nodes');
    
    if (nodesHandlerIndex !== -1) {
      const handler = [...dataset[nodesHandlerIndex]];
      const props = { ...handler[1] };
      const selectors = props.selector ? 
        (Array.isArray(props.selector) ? [...props.selector] : [props.selector]) : 
        [];
      selectors.push('new-selector');
      props.selector = selectors;
      handler[1] = props;
      dataset[nodesHandlerIndex] = handler;
      updateWorkingQuestion('dataset', dataset);
    }
  }
  
  function addFirstSelector() {
    const dataset = [...question.dataset];
    const nodesHandlerIndex = dataset.findIndex(handler => handler[0] === 'nodes');
    
    if (nodesHandlerIndex !== -1) {
      const handler = [...dataset[nodesHandlerIndex]];
      const props = { ...handler[1] };
      props.selector = 'node-selector';
      handler[1] = props;
      dataset[nodesHandlerIndex] = handler;
      updateWorkingQuestion('dataset', dataset);
    }
  }
  
  function removeAllSelectors() {
    if (confirm('Remove all selector values?')) {
      const dataset = [...question.dataset];
      const nodesHandlerIndex = dataset.findIndex(handler => handler[0] === 'nodes');
      
      if (nodesHandlerIndex !== -1) {
        const handler = [...dataset[nodesHandlerIndex]];
        const props = { ...handler[1] };
        delete props.selector;
        handler[1] = props;
        dataset[nodesHandlerIndex] = handler;
        updateWorkingQuestion('dataset', dataset);
      }
    }
  }
  
  $: question, selectorValues = getNodeSelectorValues();
  $: question, hasSelector = selectorValues.length > 0;
</script>

<div class="field-wrapper">
  <label>
    Node Selector Values
    {#if hasSelector}
      <span class="selector-count-badge">{selectorValues.length} selector{selectorValues.length !== 1 ? 's' : ''}</span>
    {/if}
  </label>
  
  {#if hasSelector}
    <div class="selector-values-editor">
      {#each selectorValues as selectorValue, idx}
        <div class="selector-item">
          <div class="selector-input">
            <input 
              type="text"
              value={selectorValue}
              on:input={(e) => updateNodeSelectorValue(idx, e.target.value)}
              placeholder="e.g., filter-key, category, group"
              class="selector-field"
            />
          </div>
          <div class="selector-actions">
            <button 
              class="btn btn-small btn-danger"
              on:click={() => removeNodeSelectorValue(idx)}
              title="Remove selector"
            >
              ×
            </button>
          </div>
        </div>
      {/each}
      
      <div class="selector-footer">
        <button class="btn btn-secondary" on:click={addNodeSelectorValue}>
          Add Selector Value
        </button>
        <button class="btn btn-warning btn-small" on:click={removeAllSelectors}>
          Remove All
        </button>
      </div>
    </div>
  {:else}
    <div class="no-selector">
      <div class="no-selector-content">
        <p class="no-selector-text">No selector values defined</p>
        <p class="no-selector-description">
          Selectors are used for filtering or categorizing nodes in the interface
        </p>
        <button class="btn btn-secondary" on:click={addFirstSelector}>
          Add First Selector
        </button>
      </div>
    </div>
  {/if}
</div>

<style lang="scss">
  .field-wrapper {
    margin-bottom: 20px;
    
    > label {
      display: flex;
      align-items: center;
      gap: 8px;
      font-weight: bold;
      margin-bottom: 8px;
      font-size: 0.8rem;
      color: #374151;
    }
  }
  
  .selector-count-badge {
    background: #8b5cf6;
    color: white;
    padding: 2px 6px;
    border-radius: 10px;
    font-size: 9px;
    font-weight: bold;
    letter-spacing: 0.5px;
  }
  
  .selector-values-editor {
    border: 2px solid #e5e7eb;
    border-radius: 6px;
    padding: 10px;
    background: rgba(255, 255, 255, 0.8);
  }
  
  .selector-item {
    display: flex;
    gap: 8px;
    align-items: center;
    margin-bottom: 8px;
    padding: 8px;
    border-radius: 4px;
    transition: all 0.2s ease;
    
    &:hover {
      background: rgba(139, 92, 246, 0.05);
    }
    
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  .selector-input {
    flex: 1;
    
    .selector-field {
      width: 100%;
      padding: 6px 10px;
      border: 1px solid #d1d5db;
      border-radius: 4px;
      background: #fff;
      font-size: 0.75rem;
      font-weight: 500;
      
      &:focus {
        outline: none;
        border-color: #8b5cf6;
        box-shadow: 0 0 0 1px #8b5cf6;
      }
      
      &::placeholder {
        color: #9ca3af;
        font-weight: normal;
      }
    }
  }
  
  .selector-actions {
    display: flex;
    align-items: center;
    flex: 0 0 auto;
  }
  
  .selector-footer {
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px solid #e5e7eb;
    display: flex;
    gap: 8px;
    align-items: center;
    justify-content: center;
  }
  
  .no-selector {
    border: 2px dashed #d1d5db;
    border-radius: 6px;
    padding: 20px;
    background: #f9fafb;
    text-align: center;
    
    .no-selector-content {
      .no-selector-text {
        margin-bottom: 8px;
        color: #6b7280;
        font-weight: 500;
        font-size: 0.8rem;
      }
      
      .no-selector-description {
        margin-bottom: 15px;
        color: #9ca3af;
        font-size: 0.75rem;
        font-style: italic;
        line-height: 1.4;
      }
    }
  }
  
  .btn {
    padding: 4px 8px;
    border: none;
    border-radius: 3px;
    cursor: pointer;
    font-size: 11px;
    font-weight: bold;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    transition: all 0.2s ease;
    
    &.btn-secondary {
      background: #8b5cf6;
      color: white;
      
      &:hover {
        background: #7c3aed;
      }
    }
    
    &.btn-danger {
      background: #ef4444;
      color: white;
      
      &:hover {
        background: #dc2626;
      }
    }
    
    &.btn-warning {
      background: #f59e0b;
      color: white;
      
      &:hover {
        background: #d97706;
      }
    }
    
    &.btn-small {
      padding: 4px 6px;
      font-size: 10px;
    }
  }
</style>